import {
  LEAD_SOURCE_CALENDAR,
  LEAD_STAGE_CURRENT,
  NODE_DATA_CALENDAR_DISMISSED_KEY,
  NODE_DATA_LEAD_STAGE_KEY,
  type GraphChangeSetAuditLogListResponse,
  type GraphChangeSetListResponse,
  type CalendarProvider,
  type GraphEdge,
  type GraphFrameCommitResponse,
  type GraphNode,
  type GraphRelationType,
  type GraphVisualizationFrame,
  type MeetingPreparationRequest
} from "@salesko/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  ChevronRight,
  Clock,
  Command,
  Download,
  EyeOff,
  GitBranch,
  GitMerge,
  List,
  Link2,
  Loader2,
  NotebookPen,
  Pencil,
  Plus,
  Route,
  Save,
  Split,
  Trash2,
  Undo2,
  UserRound
} from "lucide-react";
import { AgentAvatar, Avatar, Badge } from "@salesko/design-system";
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { useI18n, type Dictionary } from "../../i18n";
import { dismissCalendarEvent, MeetingPreparationApiError, prepareMeetingBrief } from "../../lib/api";
import { entityMeta } from "../entityMeta";
import { entityLabel, entitySingularLabel, relationshipTypeLabel } from "../i18n";
import {
  canonicalMergeTargetSuggestions,
  canonicalEntityIdForGraphNode,
  canonicalIdentityDraftFromNode,
  type CanonicalIdentityDraft
} from "../lib/canonicalIdentity";
import { canonicalRegistryViewForDetailTab } from "../lib/canonicalRegistry";
import { detailAbout, detailSubtitle, detailTags, initialsOf } from "../lib/detail";
import { compactDateTime, shortChangeSetId } from "../lib/format";
import type { NodeDataFieldKey, NodeEditDraft } from "../lib/nodeEdit";
import { hasNodeEditChanges, nodeDataFieldSpecsByType, nodeEditDraftFromNode } from "../lib/nodeEdit";
import type { RelationshipCreateDraft } from "../lib/relationshipCreate";
import {
  hasRelationshipEditChanges,
  relationshipCreateDraftFromFrame,
  relationshipEditDraftFromEdge,
  relationshipLabelForType,
  relationshipTypeOptions,
  type RelationshipEditDraft
} from "../lib/relationshipCreate";
import { reminderWorkspaceAction } from "../lib/relationshipReminders";
import { reviewItemsForFrame, reviewItemsForNode, type ReviewReason } from "../lib/review";
import type {
  ChangeSetActionVariables,
  ChangeSetAuditExportState,
  ChangeSetAuditLogState,
  ChangeSetHistoryState,
  CanonicalIdentityAction,
  CanonicalIdentityState,
  DetailTab,
  RelationshipIntelligenceState
} from "../types";
import { DataTable } from "./data/DataTable";
import type { DataTableColumnDef } from "./data/tableTypes";
import { useColumnVisibility } from "./data/useColumnVisibility";
import {
  canPrepareMeeting,
  MeetingPreparationPanel,
  panelStateFor,
  type MeetingPreparationMutationSnapshot
} from "./MeetingPreparationPanel";
import { PersonDetailOverview, PersonPersonalDetails } from "./PersonDetailOverview";

type ChangeSetHistoryTableRow = {
  changeSet: GraphChangeSetListResponse["items"][number];
  commandCount: number;
  idLabel: string;
  reason: string;
  statusTime: string;
};

type AuditEventDetail = {
  key: string;
  label: string;
  value: string;
};

type AuditEventTableRow = {
  details: AuditEventDetail[];
  entry: GraphChangeSetAuditLogListResponse["items"][number];
  eventLabel: string;
  idLabel: string;
  timeLabel: string;
};

type DetailPanelProps = {
  changeSetAuditExport: ChangeSetAuditExportState;
  changeSetAuditLog: ChangeSetAuditLogState;
  changeSetHistory: ChangeSetHistoryState;
  canonicalIdentityPending: boolean;
  canonicalIdentityState: CanonicalIdentityState;
  commit: GraphFrameCommitResponse | null;
  detailTab: DetailTab;
  frame: GraphVisualizationFrame | null;
  hasSession: boolean;
  isSelfNode: boolean;
  linkedNodes: GraphNode[];
  node: GraphNode | null;
  onClearSelfNode: () => void;
  onRequestSearch: () => void;
  onSetSelfNode: () => void;
  selectedEdgeId: string | null;
  changeSetCreatePending: boolean;
  canOpenCanonicalRegistry: boolean;
  onAcceptReview: (nodeId: string) => void;
  onApplyChangeSet: (variables: ChangeSetActionVariables) => void;
  onCreateNodeEdit: (node: GraphNode, draft: NodeEditDraft) => void;
  onCreateRelationship: (sourceNode: GraphNode, draft: RelationshipCreateDraft) => void;
  onDeleteRelationship: (edge: GraphEdge) => void;
  onEditRelationship: (edge: GraphEdge, draft: RelationshipEditDraft) => void;
  onDiscardReview: (nodeId: string) => void;
  onExportChangeSetAudit: (frameRecordId: string, changeSetId: string) => void;
  onInspectChangeSetAudit: (frameRecordId: string, changeSetId: string) => void;
  onLogReminderTouchpoint: (edge: GraphEdge) => void;
  onMergeCanonicalIdentity: (sourceNode: GraphNode, targetNode: GraphNode) => void;
  onOpenReminderFrame: (target: { frameRecordId: string; nodeId: string }) => void;
  onRefreshChangeSets: (frameRecordId: string) => void;
  onRekeyCanonicalIdentity: (sourceNode: GraphNode, label: string) => void;
  onRelationshipPathTargetChange: (nodeId: string) => void;
  onOpenCanonicalRegistry: (tab: DetailTab) => void;
  onSelectNode: (nodeId: string) => void;
  onSplitCanonicalIdentity: (sourceNode: GraphNode, label: string) => void;
  onTabChange: (tab: DetailTab) => void;
  onToggleLeadStage: (node: GraphNode, target: "current" | null) => void;
  /** Name of the tenant's Private Agent, for change-set provenance. Null when none exists. */
  privateAgentName: string | null;
  readOnly: boolean;
  relationshipIntelligence: RelationshipIntelligenceState;
  /** Host-built "Research with <agent>" action; null when the entity or state does not offer it. */
  researchTrigger: ReactNode;
  reviewActionPending: boolean;
  tabs: Array<{ count: number; id: DetailTab }>;
};

export function DetailPanel({
  changeSetAuditExport,
  changeSetAuditLog,
  changeSetHistory,
  canonicalIdentityPending,
  canonicalIdentityState,
  commit,
  detailTab,
  frame,
  hasSession,
  isSelfNode,
  linkedNodes,
  node,
  onClearSelfNode,
  onRequestSearch,
  onSetSelfNode,
  selectedEdgeId,
  changeSetCreatePending,
  canOpenCanonicalRegistry,
  onAcceptReview,
  onApplyChangeSet,
  onCreateNodeEdit,
  onCreateRelationship,
  onDeleteRelationship,
  onEditRelationship,
  onDiscardReview,
  onExportChangeSetAudit,
  onInspectChangeSetAudit,
  onLogReminderTouchpoint,
  onMergeCanonicalIdentity,
  onOpenCanonicalRegistry,
  onOpenReminderFrame,
  onRefreshChangeSets,
  onRekeyCanonicalIdentity,
  onRelationshipPathTargetChange,
  onSelectNode,
  onSplitCanonicalIdentity,
  onTabChange,
  onToggleLeadStage,
  privateAgentName,
  readOnly,
  relationshipIntelligence,
  researchTrigger,
  reviewActionPending,
  tabs
}: DetailPanelProps) {
  const { t } = useI18n();
  const [nodeEditOpen, setNodeEditOpen] = useState(false);
  const [nodeEditDraft, setNodeEditDraft] = useState<NodeEditDraft>(() => nodeEditDraftFromNode(node));
  const [relationshipCreateOpen, setRelationshipCreateOpen] = useState(false);
  const [relationshipDraft, setRelationshipDraft] = useState<RelationshipCreateDraft>(() =>
    relationshipCreateDraftFromFrame(frame, node)
  );
  const [relationshipEditEdgeId, setRelationshipEditEdgeId] = useState<string | null>(null);
  const [relationshipEditDraft, setRelationshipEditDraft] = useState<RelationshipEditDraft>(() =>
    relationshipEditDraftFromEdge(null)
  );
  const [identityOpen, setIdentityOpen] = useState(false);
  const [identityDraft, setIdentityDraft] = useState<CanonicalIdentityDraft>(() => identityDraftFor(frame, node));

  useEffect(() => {
    setNodeEditOpen(false);
    setNodeEditDraft(nodeEditDraftFromNode(node));
  }, [node]);

  useEffect(() => {
    setRelationshipCreateOpen(false);
    setRelationshipDraft(relationshipCreateDraftFromFrame(frame, node));
    setRelationshipEditEdgeId(null);
    setRelationshipEditDraft(relationshipEditDraftFromEdge(null));
  }, [frame?.frameId, frame?.graph.nodes, node?.id]);

  useEffect(() => {
    setIdentityOpen(false);
    setIdentityDraft(identityDraftFor(frame, node));
  }, [frame?.frameId, frame?.graph.nodes, node?.id]);

  const meetingPreparationMutation = useMutation({
    mutationFn: (variables: MeetingPreparationRequest) => prepareMeetingBrief(variables)
  });

  useEffect(() => {
    // Switching to a different node clears a prior brief/error; in-place edits to
    // the same node (a new node object, same id) must not, so this depends on
    // node?.id alone — the same identity-only key relationshipDraft/identityDraft
    // resets above use, not nodeEditDraft's whole-object [node] key.
    meetingPreparationMutation.reset();
  }, [node?.id]);

  const queryClient = useQueryClient();
  const calendarDismissMutation = useMutation({
    mutationFn: ({ providerEventId, provider }: { providerEventId: string; provider: CalendarProvider | undefined }) =>
      dismissCalendarEvent(providerEventId, provider ? { provider } : {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["graph-frame"] });
      void queryClient.invalidateQueries({ queryKey: ["graph-frames"] });
      void queryClient.invalidateQueries({ queryKey: ["graph-node-search"] });
    }
  });

  if (!node) {
    return (
      <div className="detail-empty">
        <span className="detail-empty-mark">
          <Command size={28} />
        </span>
        <strong>{t.detail.emptyTitle}</strong>
        <button className="secondary-button" type="button" onClick={onRequestSearch}>
          {t.workspace.searchAriaLabel}
        </button>
      </div>
    );
  }

  const meta = entityMeta[node.type];
  const Icon = meta.icon;
  const about = detailAbout(node, frame);
  const tags = detailTags(node, frame);
  const entityTypeLabel = entitySingularLabel(t, node.type);
  const subtitle = detailSubtitle(node, frame, t);
  // detailSubtitle falls back to the entity type, which the eyebrow above already
  // states. Other consumers (search results, linked rows) need that fallback; here
  // it would only print the same words twice.
  const subtitleLine = subtitle === entityTypeLabel ? null : subtitle;
  const confidencePercent = Math.round(node.confidence * 100);
  const isDefaultPreview = frame?.frameId === "default-preview" && !commit;
  const reviewItems = frame ? reviewItemsForNode(frame, node) : [];
  const frameReviewCount = frame ? reviewItemsForFrame(frame).length : 0;
  const canCreateGraphChange = !readOnly && Boolean(commit) && !isDefaultPreview;
  const canCreateNodeEdit = canCreateGraphChange;
  const isCurrentLead = node.data[NODE_DATA_LEAD_STAGE_KEY] === LEAD_STAGE_CURRENT;
  // Calendar-sourced event nodes carry data.leadSource === "calendar_connector"
  // and a providerEventId; the dismiss path is a self-contained MVP affordance
  // (button + invalidate/refetch) that soft-hides the node from the graph view
  // via data.calendarDismissed=true. Shown only for undismissed calendar events.
  const calendarProviderEventId = typeof node.data.providerEventId === "string" ? node.data.providerEventId : null;
  const calendarDismissed = node.data[NODE_DATA_CALENDAR_DISMISSED_KEY] === true;
  const isCalendarEvent =
    node.type === "event" &&
    node.data.leadSource === LEAD_SOURCE_CALENDAR &&
    calendarProviderEventId !== null &&
    !calendarDismissed;
  const calendarProviderValue = node.data.calendarProvider;
  const calendarProvider: CalendarProvider | undefined =
    calendarProviderValue === "google" || calendarProviderValue === "microsoft" ? calendarProviderValue : undefined;
  const canPrepareMeetingBrief = canPrepareMeeting({ hasSession, node, readOnly });
  // The node this render is showing, in prepareMeetingBrief's request shape.
  // panelStateFor only projects the mutation's cached result onto the panel
  // when mutation.variables matches this — see panelStateFor's doc comment
  // for why: the mutation's own reset (below) is a passive effect that runs
  // after this render, so without this gate the first render after switching
  // from an already-succeeded event to a different one would still show the
  // previous event's stale brief for one frame.
  const currentMeetingPreparationKey =
    calendarProvider && calendarProviderEventId ? { provider: calendarProvider, providerEventId: calendarProviderEventId } : null;
  let meetingPreparationSnapshot: MeetingPreparationMutationSnapshot = { status: "idle" };
  if (meetingPreparationMutation.status === "pending") {
    meetingPreparationSnapshot = { status: "pending", variables: meetingPreparationMutation.variables };
  } else if (meetingPreparationMutation.status === "success") {
    meetingPreparationSnapshot = {
      status: "success",
      data: meetingPreparationMutation.data,
      variables: meetingPreparationMutation.variables
    };
  } else if (meetingPreparationMutation.status === "error") {
    // error.code is null for a transport-level failure (fetch TypeError, a
    // Cloudflare edge timeout/524 returning HTML, a success-body schema
    // drift) rather than a documented meeting_preparation_* code. That is
    // still a real failure the user needs to see — MeetingPreparationPanel
    // renders it as a generic, cause-agnostic error with Retry rather than
    // this silently staying on idle.
    const meetingPreparationErrorCode =
      meetingPreparationMutation.error instanceof MeetingPreparationApiError
        ? meetingPreparationMutation.error.code
        : null;
    meetingPreparationSnapshot = {
      status: "error",
      code: meetingPreparationErrorCode,
      variables: meetingPreparationMutation.variables
    };
  }
  const meetingPreparationState = panelStateFor(meetingPreparationSnapshot, currentMeetingPreparationKey);
  const triggerMeetingPreparation = () => {
    if (calendarProvider && calendarProviderEventId) {
      meetingPreparationMutation.mutate({ provider: calendarProvider, providerEventId: calendarProviderEventId });
    }
  };
  const nodeEditChanged = hasNodeEditChanges(node, nodeEditDraft);
  const nodeEditValid = nodeEditDraft.label.trim().length > 0;
  const relationshipTargetNodes = frame?.graph.nodes.filter((candidate) => candidate.id !== node.id) ?? [];
  const canCreateRelationship = canCreateGraphChange && relationshipTargetNodes.length > 0;
  const relationshipValid =
    relationshipDraft.targetNodeId.length > 0 &&
    relationshipDraft.targetNodeId !== node.id &&
    relationshipTargetNodes.some((candidate) => candidate.id === relationshipDraft.targetNodeId);
  const incidentEdges = frame?.graph.edges.filter((edge) => edge.source === node.id || edge.target === node.id) ?? [];
  const editingRelationshipEdge = incidentEdges.find((edge) => edge.id === relationshipEditEdgeId) ?? null;
  const relationshipEditChanged = editingRelationshipEdge
    ? hasRelationshipEditChanges(editingRelationshipEdge, relationshipEditDraft)
    : false;
  const sourceCanonicalEntityId = canonicalEntityIdForGraphNode(node);
  const mergeTargetSuggestions = canonicalMergeTargetSuggestions(frame, node);
  const selectedMergeTargetSuggestion =
    mergeTargetSuggestions.find((suggestion) => suggestion.node.id === identityDraft.mergeTargetNodeId) ?? null;
  const selectedMergeTarget = selectedMergeTargetSuggestion?.node ?? null;
  const canEditCanonicalIdentity = canCreateGraphChange;
  const splitLabel = identityDraft.splitLabel.trim();
  const rekeyLabel = identityDraft.rekeyLabel.trim();
  const splitValid =
    splitLabel.length > 0 && canonicalEntityIdForGraphNode({ label: splitLabel, type: node.type }) !== sourceCanonicalEntityId;
  const rekeyValid =
    rekeyLabel.length > 0 && canonicalEntityIdForGraphNode({ label: rekeyLabel, type: node.type }) !== sourceCanonicalEntityId;
  const activeLinkedTab = tabs.find((tab) => tab.id === detailTab) ?? null;
  const canViewAllLinkedEntities =
    canOpenCanonicalRegistry &&
    Boolean(activeLinkedTab) &&
    (activeLinkedTab?.count ?? 0) > 0 &&
    Boolean(canonicalRegistryViewForDetailTab(detailTab));

  const submitNodeEdit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canCreateNodeEdit || !nodeEditChanged || !nodeEditValid || changeSetCreatePending) {
      return;
    }

    onCreateNodeEdit(node, nodeEditDraft);
    setNodeEditOpen(false);
  };

  const submitRelationshipCreate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canCreateRelationship || !relationshipValid || changeSetCreatePending) {
      return;
    }

    onCreateRelationship(node, relationshipDraft);
    setRelationshipCreateOpen(false);
  };

  const submitRelationshipEdit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingRelationshipEdge || !relationshipEditChanged || changeSetCreatePending) {
      return;
    }

    onEditRelationship(editingRelationshipEdge, relationshipEditDraft);
    setRelationshipEditEdgeId(null);
  };

  const submitIdentityMerge = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEditCanonicalIdentity || canonicalIdentityPending || !selectedMergeTarget) {
      return;
    }

    onMergeCanonicalIdentity(node, selectedMergeTarget);
  };

  const submitIdentityRekey = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEditCanonicalIdentity || canonicalIdentityPending || !rekeyValid) {
      return;
    }

    onRekeyCanonicalIdentity(node, rekeyLabel);
  };

  const submitIdentitySplit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEditCanonicalIdentity || canonicalIdentityPending || !splitValid) {
      return;
    }

    onSplitCanonicalIdentity(node, splitLabel);
  };

  const updateRelationshipType = (nextType: GraphRelationType) => {
    setRelationshipDraft((current) => ({
      ...current,
      label:
        current.label.trim() === "" || current.label === relationshipLabelForType(current.type)
          ? relationshipLabelForType(nextType)
          : current.label,
      type: nextType
    }));
  };

  const updateRelationshipEditType = (nextType: GraphRelationType) => {
    setRelationshipEditDraft((current) => ({
      ...current,
      label:
        current.label.trim() === "" || current.label === relationshipLabelForType(current.type)
          ? relationshipLabelForType(nextType)
          : current.label,
      type: nextType
    }));
  };

  return (
    <>
      <div className={node.type === "person" ? "detail-header detail-header-person" : "detail-header"}>
        {node.type === "person" ? (
          <Avatar
            glow
            initials={initialsOf(node.label)}
            size="xl"
            tone={meta.tone as "person" | "company" | "event" | "opportunity" | "note"}
          />
        ) : (
          <Avatar
            glow
            icon={<Icon size={30} />}
            size="xl"
            tone={meta.tone as "person" | "company" | "event" | "opportunity" | "note"}
          />
        )}
        <div className="detail-title-block">
          <p className="eyebrow">{entityTypeLabel}</p>
          <h2>{node.label}</h2>
          {subtitleLine ? <span>{subtitleLine}</span> : null}
          {!readOnly ? (
            <button
              aria-pressed={isSelfNode}
              className={isSelfNode ? "self-node-toggle is-self" : "self-node-toggle"}
              onClick={isSelfNode ? onClearSelfNode : onSetSelfNode}
              title={
                isSelfNode
                  ? t.detail.selfMarkedTitle
                  : t.detail.selfMarkTitle
              }
              type="button"
            >
              <UserRound size={13} />
              {isSelfNode ? t.detail.selfYou : t.detail.selfSet}
            </button>
          ) : null}
          {!readOnly ? (
            <button
              aria-pressed={isCurrentLead}
              className={isCurrentLead ? "lead-stage-toggle is-current" : "lead-stage-toggle"}
              disabled={!canCreateGraphChange || changeSetCreatePending}
              onClick={() => onToggleLeadStage(node, isCurrentLead ? null : "current")}
              title={
                !canCreateGraphChange
                  ? t.detail.commitBeforeEditNodesTitle
                  : isCurrentLead
                    ? t.detail.leadStageOutTitle
                    : t.detail.leadStageInTitle
              }
              type="button"
            >
              <CheckCircle2 size={13} />
              {isCurrentLead ? t.detail.leadStageOutLabel : t.detail.leadStageInLabel}
            </button>
          ) : null}
          {isCalendarEvent ? (
            <button
              className="lead-stage-toggle"
              disabled={calendarDismissMutation.isPending}
              onClick={() => {
                if (calendarProviderEventId !== null) {
                  calendarDismissMutation.mutate({ providerEventId: calendarProviderEventId, provider: calendarProvider });
                }
              }}
              title={t.detail.dismissCalendarEvent}
              type="button"
            >
              {calendarDismissMutation.isPending ? <Loader2 size={13} /> : <EyeOff size={13} />}
              {calendarDismissMutation.isPending ? t.detail.dismissCalendarEventPending : t.detail.dismissCalendarEvent}
            </button>
          ) : null}
          {canPrepareMeetingBrief ? (
            <button
              aria-label={t.meetingPreparation.prepareActionTitle}
              className="lead-stage-toggle"
              disabled={meetingPreparationMutation.isPending}
              onClick={triggerMeetingPreparation}
              title={t.meetingPreparation.prepareActionTitle}
              type="button"
            >
              {meetingPreparationMutation.isPending ? <Loader2 size={13} /> : <NotebookPen size={13} />}
              {meetingPreparationMutation.isPending ? t.meetingPreparation.preparePending : t.meetingPreparation.prepareAction}
            </button>
          ) : null}
        </div>
      </div>

      {node.type === "person" ? (
        <PersonDetailOverview
          changeSetCreatePending={changeSetCreatePending}
          currentFrameRecordId={readOnly ? null : commit?.id ?? null}
          frame={frame}
          node={node}
          onLogReminderTouchpoint={onLogReminderTouchpoint}
          onOpenReminderFrame={onOpenReminderFrame}
          readOnly={readOnly}
          relationshipIntelligence={relationshipIntelligence}
          tags={tags}
        />
      ) : tags.length > 0 ? (
        <div className="tag-row">
          {tags.map((tag) => (
            <Badge key={tag} tone={meta.tone as "person" | "company" | "event" | "opportunity" | "note"}>
              {tag}
            </Badge>
          ))}
        </div>
      ) : null}

      {researchTrigger}

      <section className="detail-section">
        <div className="section-heading-row">
          <p className="eyebrow">{!nodeEditOpen && about?.kind === "evidence" ? t.detail.source : t.detail.about}</p>
          {!readOnly ? (
            <button
              className="history-refresh"
              disabled={!canCreateNodeEdit || changeSetCreatePending}
              onClick={() => setNodeEditOpen((current) => !current)}
              title={canCreateNodeEdit ? t.detail.editNodeTitle : t.detail.commitBeforeEditNodesTitle}
              type="button"
              aria-label={t.detail.editNodeAriaLabel}
            >
              {changeSetCreatePending ? <Loader2 className="spin" size={14} /> : <Pencil size={14} />}
            </button>
          ) : null}
        </div>
        {nodeEditOpen ? (
          <form className="node-edit-form" onSubmit={submitNodeEdit}>
            <label>
              <span>{t.detail.labelField}</span>
              <input
                value={nodeEditDraft.label}
                onChange={(event) => setNodeEditDraft((current) => ({ ...current, label: event.target.value }))}
                maxLength={120}
              />
            </label>
            <label>
              <span>{t.detail.summaryField}</span>
              <textarea
                value={nodeEditDraft.summary}
                onChange={(event) => setNodeEditDraft((current) => ({ ...current, summary: event.target.value }))}
                maxLength={500}
                rows={3}
              />
            </label>
            <label>
              <span>{t.detail.evidenceField}</span>
              <textarea
                value={nodeEditDraft.evidence}
                onChange={(event) => setNodeEditDraft((current) => ({ ...current, evidence: event.target.value }))}
                maxLength={500}
                rows={3}
              />
            </label>
            <div className="node-data-grid">
              {nodeDataFieldSpecsByType[node.type].map((spec) => {
                const setFieldValue = (value: string) =>
                  setNodeEditDraft((current) => ({
                    ...current,
                    dataFields: { ...current.dataFields, [spec.key]: value }
                  }));
                return (
                  <label key={spec.key}>
                    <span>{nodeDataFieldLabel(spec.key, t)}</span>
                    {spec.kind === "select" ? (
                      <select
                        value={nodeEditDraft.dataFields[spec.key]}
                        onChange={(event) => setFieldValue(event.target.value)}
                      >
                        <option value="">{t.detail.fieldOptionUnset}</option>
                        {(spec.options ?? []).map((option) => (
                          <option key={option} value={option}>
                            {fieldOptionLabel(spec.key, option, t)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={spec.kind === "date" ? "date" : "text"}
                        value={nodeEditDraft.dataFields[spec.key]}
                        onChange={(event) => setFieldValue(event.target.value)}
                        maxLength={spec.kind === "date" ? undefined : 160}
                      />
                    )}
                  </label>
                );
              })}
            </div>
            <div className="node-edit-actions">
              <button
                className="secondary-button"
                disabled={!nodeEditChanged || !nodeEditValid || changeSetCreatePending}
                type="submit"
              >
                {changeSetCreatePending ? <Loader2 className="spin" size={15} /> : <Save size={15} />}
                <span>{t.detail.saveAsChangeSet}</span>
              </button>
              <button className="secondary-button" type="button" onClick={() => setNodeEditOpen(false)}>
                <Undo2 size={15} />
                <span>{t.common.cancel}</span>
              </button>
            </div>
          </form>
        ) : (
          <>
            {about ? <p>{about.text}</p> : <p className="empty-line">{t.detail.noAboutYet}</p>}
            {node.type !== "person" ? (() => {
              const displayFields = nodeDisplayFields(node, t);
              return displayFields.length > 0 ? (
                <dl className="node-attributes">
                  {displayFields.map((field) => (
                    <div className="node-attribute" key={field.key}>
                      <dt>{field.label}</dt>
                      <dd>{field.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : null;
            })() : null}
          </>
        )}
      </section>

      {node.type === "person" ? <PersonPersonalDetails node={node} /> : null}

      {!readOnly && reviewItems.length > 0 ? (
        <section className="detail-section review-panel" aria-label={t.detail.reviewCandidateAriaLabel}>
          <div className="review-panel-heading">
            <AlertCircle size={18} />
            <div>
              <p className="eyebrow">{t.detail.review}</p>
              <strong>{t.detail.reviewCount(frameReviewCount)}</strong>
            </div>
          </div>
          <div className="review-reasons">
            {reviewItems.flatMap((item) =>
              item.reasons.map((reason, index) => (
                <p key={`${item.nodeId}:${index}`}>{reviewReasonLabel(reason, t)}</p>
              ))
            )}
            {node.evidence ? <small>{node.evidence}</small> : null}
          </div>
          <div className="review-actions">
            <button className="secondary-button" disabled={reviewActionPending} type="button" onClick={() => onAcceptReview(node.id)}>
              <CheckCircle2 size={15} />
              <span>{t.detail.accept}</span>
            </button>
            <button
              className="secondary-button review-discard-button"
              disabled={reviewActionPending}
              type="button"
              onClick={() => onDiscardReview(node.id)}
            >
              <Undo2 size={15} />
              <span>{t.detail.discard}</span>
            </button>
          </div>
        </section>
      ) : null}

      <section className="detail-section">
        <div className="section-heading-row">
          <p className="eyebrow">{t.detail.linkedEntities}</p>
          <span className="linked-heading-actions">
            {canViewAllLinkedEntities && activeLinkedTab ? (
              <button
                className="view-all-linked-button"
                onClick={() => onOpenCanonicalRegistry(detailTab)}
                title={t.detail.viewAllLinkedTitle(detailTabLabel(t, activeLinkedTab.id).toLowerCase())}
                type="button"
                aria-label={t.detail.viewAllLinkedAriaLabel(activeLinkedTab.count, detailTabLabel(t, activeLinkedTab.id).toLowerCase())}
              >
                <List size={14} />
                <span>{t.detail.viewAllLinked(activeLinkedTab.count)}</span>
              </button>
            ) : null}
            {!readOnly ? (
              <button
                className="history-refresh"
                disabled={!canCreateRelationship || changeSetCreatePending}
                onClick={() => setRelationshipCreateOpen((current) => !current)}
                title={canCreateRelationship ? t.detail.createRelationshipTitle : t.detail.commitBeforeCreateRelationshipTitle}
                type="button"
                aria-label={t.detail.createRelationshipAriaLabel}
              >
                {changeSetCreatePending ? <Loader2 className="spin" size={14} /> : <Link2 size={14} />}
              </button>
            ) : null}
          </span>
        </div>
        {relationshipCreateOpen ? (
          <form className="relationship-create-form" onSubmit={submitRelationshipCreate}>
            <label>
              <span>{t.detail.targetField}</span>
              <select
                value={relationshipDraft.targetNodeId}
                onChange={(event) =>
                  setRelationshipDraft((current) => ({ ...current, targetNodeId: event.target.value }))
                }
              >
                {relationshipTargetNodes.map((targetNode) => (
                  <option key={targetNode.id} value={targetNode.id}>
                    {targetNode.label} · {entitySingularLabel(t, targetNode.type)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t.detail.typeField}</span>
              <select
                value={relationshipDraft.type}
                onChange={(event) => updateRelationshipType(event.target.value as GraphRelationType)}
              >
                {relationshipTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {relationshipTypeLabel(t, option.value)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t.detail.labelField}</span>
              <input
                value={relationshipDraft.label}
                onChange={(event) => setRelationshipDraft((current) => ({ ...current, label: event.target.value }))}
                maxLength={120}
              />
            </label>
            <label>
              <span>{t.detail.evidenceField}</span>
              <textarea
                value={relationshipDraft.evidence}
                onChange={(event) => setRelationshipDraft((current) => ({ ...current, evidence: event.target.value }))}
                maxLength={500}
                rows={3}
              />
            </label>
            <label>
              <span>{t.detail.strengthField(Math.round(relationshipDraft.strength * 100))}</span>
              <input
                type="range"
                min={0.1}
                max={1}
                step={0.05}
                value={relationshipDraft.strength}
                onChange={(event) =>
                  setRelationshipDraft((current) => ({ ...current, strength: Number(event.target.value) }))
                }
              />
            </label>
            <div className="relationship-create-actions">
              <button
                className="secondary-button"
                disabled={!relationshipValid || changeSetCreatePending}
                type="submit"
              >
                {changeSetCreatePending ? <Loader2 className="spin" size={15} /> : <Plus size={15} />}
                <span>{t.detail.saveRelationship}</span>
              </button>
              <button className="secondary-button" type="button" onClick={() => setRelationshipCreateOpen(false)}>
                <Undo2 size={15} />
                <span>{t.common.cancel}</span>
              </button>
            </div>
          </form>
        ) : null}
        {editingRelationshipEdge ? (
          <form className="relationship-edit-form" onSubmit={submitRelationshipEdit}>
            <p className="relationship-edit-context">
              {t.detail.editingRelationship(edgeEndpointLabel(editingRelationshipEdge, node.id, frame), editingRelationshipEdge.id)}
            </p>
            <label>
              <span>{t.detail.typeField}</span>
              <select
                value={relationshipEditDraft.type}
                onChange={(event) => updateRelationshipEditType(event.target.value as GraphRelationType)}
              >
                {relationshipTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {relationshipTypeLabel(t, option.value)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t.detail.labelField}</span>
              <input
                value={relationshipEditDraft.label}
                onChange={(event) =>
                  setRelationshipEditDraft((current) => ({ ...current, label: event.target.value }))
                }
                maxLength={120}
              />
            </label>
            <label>
              <span>{t.detail.evidenceField}</span>
              <textarea
                value={relationshipEditDraft.evidence}
                onChange={(event) =>
                  setRelationshipEditDraft((current) => ({ ...current, evidence: event.target.value }))
                }
                maxLength={500}
                rows={3}
              />
            </label>
            <label>
              <span>{t.detail.strengthField(Math.round(relationshipEditDraft.strength * 100))}</span>
              <input
                type="range"
                min={0.1}
                max={1}
                step={0.05}
                value={relationshipEditDraft.strength}
                onChange={(event) =>
                  setRelationshipEditDraft((current) => ({ ...current, strength: Number(event.target.value) }))
                }
              />
            </label>
            <div className="relationship-create-actions">
              <button
                className="secondary-button"
                disabled={!relationshipEditChanged || changeSetCreatePending}
                type="submit"
              >
                {changeSetCreatePending ? <Loader2 className="spin" size={15} /> : <Save size={15} />}
                <span>{t.detail.saveRelationshipChanges}</span>
              </button>
              <button className="secondary-button" type="button" onClick={() => setRelationshipEditEdgeId(null)}>
                <Undo2 size={15} />
                <span>{t.common.cancel}</span>
              </button>
            </div>
          </form>
        ) : null}
        {incidentEdges.length > 0 ? (
          <div className="relationship-edge-list" aria-label={t.detail.existingRelationshipsAriaLabel}>
            {incidentEdges.map((edge) => {
              const isSelectedRelationship = edge.id === selectedEdgeId;
              const edgeTypeLabel = relationshipTypeLabel(t, edge.type);
              // edge.label is only worth a second line when it says something the
              // relationship type does not — the stable default label and the
              // localized type label are both the type restated.
              const hasDistinctEdgeLabel =
                edge.label !== edgeTypeLabel && edge.label !== relationshipLabelForType(edge.type);
              const edgeDirectionMark = edge.source === node.id ? "→ " : "← ";
              return (
                <article
                  aria-current={isSelectedRelationship ? "true" : undefined}
                  className={isSelectedRelationship ? "relationship-edge-row is-selected" : "relationship-edge-row"}
                  key={edge.id}
                >
                  <span className="relationship-edge-main">
                    <strong>
                      <span aria-hidden="true">{edgeDirectionMark}</span>
                      <span className="sr-only">
                        {edge.source === node.id
                          ? t.detail.relationshipDirectionOut
                          : t.detail.relationshipDirectionIn}
                      </span>
                      {edgeEndpointLabel(edge, node.id, frame)}
                    </strong>
                    <small>
                      {edgeTypeLabel}
                      {hasDistinctEdgeLabel ? ` · ${edge.label}` : ""}
                    </small>
                  </span>
                  <button
                    aria-label={t.detail.editRelationshipAriaLabel(edge.label)}
                    className="history-refresh"
                    disabled={!canCreateGraphChange || changeSetCreatePending}
                    onClick={() => {
                      setRelationshipCreateOpen(false);
                      setRelationshipEditEdgeId(edge.id);
                      setRelationshipEditDraft(relationshipEditDraftFromEdge(edge));
                    }}
                    title={canCreateGraphChange ? t.detail.editRelationshipTitle : t.detail.commitBeforeEditRelationshipsTitle}
                    type="button"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    aria-label={t.detail.deleteRelationshipAriaLabel(edge.label)}
                    className="history-refresh relationship-delete-button"
                    disabled={!canCreateGraphChange || changeSetCreatePending}
                    onClick={() => {
                      setRelationshipCreateOpen(false);
                      setRelationshipEditEdgeId(null);
                      onDeleteRelationship(edge);
                    }}
                    title={canCreateGraphChange ? t.detail.deleteRelationshipTitle : t.detail.commitBeforeDeleteRelationshipsTitle}
                    type="button"
                  >
                    <Trash2 size={14} />
                  </button>
                </article>
              );
            })}
          </div>
        ) : null}
        <div className="tabs" role="tablist" aria-label={t.detail.linkedGroupsAriaLabel}>
          {tabs.map((tab) => (
            <button
              aria-selected={detailTab === tab.id}
              className={detailTab === tab.id ? "tab is-active" : "tab"}
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              role="tab"
              type="button"
            >
              <span>{detailTabLabel(t, tab.id)}</span>
              <strong>{tab.count}</strong>
            </button>
          ))}
        </div>

        <div className="linked-list">
          {linkedNodes.length > 0 ? (
            linkedNodes.map((linkedNode) => {
              const linkedMeta = entityMeta[linkedNode.type];
              const LinkedIcon = linkedMeta.icon;
              return (
                <button
                  className="linked-row"
                  key={linkedNode.id}
                  onClick={() => onSelectNode(linkedNode.id)}
                  type="button"
                >
                  {linkedNode.type === "person" ? (
                    <Avatar
                      initials={initialsOf(linkedNode.label)}
                      size="sm"
                      style={{ width: "30px", height: "30px", fontSize: "0.72rem", boxShadow: "0 0 0 3px rgba(45, 38, 26, 0.05)" }}
                      tone={linkedMeta.tone as "person" | "company" | "event" | "opportunity" | "note"}
                    />
                  ) : (
                    <Avatar
                      icon={<LinkedIcon size={15} />}
                      size="sm"
                      style={{ width: "30px", height: "30px", boxShadow: "0 0 0 3px rgba(45, 38, 26, 0.05)" }}
                      tone={linkedMeta.tone as "person" | "company" | "event" | "opportunity" | "note"}
                    />
                  )}
                  <span>
                    <strong>{linkedNode.label}</strong>
                    <small>{detailSubtitle(linkedNode, null, t)}</small>
                  </span>
                  <ChevronRight size={16} />
                </button>
              );
            })
          ) : (
            <p className="empty-line">{t.detail.noLinkedYet(detailTabLabel(t, detailTab).toLowerCase())}</p>
          )}
        </div>
      </section>

      {canPrepareMeetingBrief ? (
        <MeetingPreparationPanel
          onPrepare={triggerMeetingPreparation}
          onRetry={triggerMeetingPreparation}
          state={meetingPreparationState}
        />
      ) : null}

      <RelationshipIntelligencePanel
        changeSetCreatePending={changeSetCreatePending}
        currentFrameRecordId={readOnly ? null : commit?.id ?? null}
        frame={frame}
        node={node}
        onLogReminderTouchpoint={onLogReminderTouchpoint}
        onOpenReminderFrame={onOpenReminderFrame}
        onTargetChange={onRelationshipPathTargetChange}
        showReminders={node.type !== "person"}
        state={relationshipIntelligence}
      />

      {readOnly ? null : (
        <ChangeSetHistoryPanel
          auditExport={changeSetAuditExport}
          auditLog={changeSetAuditLog}
          commit={commit}
          onApply={onApplyChangeSet}
          onExportAudit={onExportChangeSetAudit}
          onInspectAudit={onInspectChangeSetAudit}
          onRefresh={onRefreshChangeSets}
          privateAgentName={privateAgentName}
          state={changeSetHistory}
        />
      )}

      <details className="detail-section detail-advanced">
        <summary className="eyebrow">{t.detail.advanced}</summary>
        <div className="detail-advanced-body">
          <dl className="node-attributes">
            <div className="node-attribute">
              <dt>{t.detail.extractionConfidence}</dt>
              <dd>{confidencePercent}%</dd>
            </div>
          </dl>

          <section className="detail-section identity-panel" aria-label={t.detail.canonicalIdentityAriaLabel}>
            <div className="section-heading-row">
              <p className="eyebrow">{t.detail.identity}</p>
              {!readOnly ? (
                <button
                  className="history-refresh"
                  disabled={!canEditCanonicalIdentity || canonicalIdentityPending}
                  onClick={() => setIdentityOpen((current) => !current)}
                  title={canEditCanonicalIdentity ? t.detail.editIdentityTitle : t.detail.commitBeforeEditIdentityTitle}
                  type="button"
                  aria-label={t.detail.editIdentityAriaLabel}
                >
                  {canonicalIdentityPending ? <Loader2 className="spin" size={14} /> : <GitBranch size={14} />}
                </button>
              ) : null}
            </div>
            <code className="identity-code">{sourceCanonicalEntityId}</code>
            {canonicalIdentityState.status === "running" ? (
              <p className="history-loading">
                <Loader2 className="spin" size={14} />
                {t.detail.identityActionRunning(identityActionLabel(canonicalIdentityState.action, t), canonicalIdentityState.sourceLabel)}
              </p>
            ) : null}
            {canonicalIdentityState.status === "success" ? (
              <p className="history-success">
                <CheckCircle2 size={14} />
                {t.detail.identityActionSaved(identityActionLabel(canonicalIdentityState.action, t))}
              </p>
            ) : null}
            {canonicalIdentityState.status === "error" ? (
              <p className="history-error">
                <AlertCircle size={14} />
                {canonicalIdentityState.message}
              </p>
            ) : null}
            {!readOnly && identityOpen ? (
              <div className="identity-actions">
                <form className="identity-action-form" onSubmit={submitIdentitySplit}>
                  <label>
                    <span>{t.detail.splitLabel}</span>
                    <input
                      maxLength={120}
                      value={identityDraft.splitLabel}
                      onChange={(event) => setIdentityDraft((current) => ({ ...current, splitLabel: event.target.value }))}
                    />
                  </label>
                  <button
                    className="secondary-button"
                    disabled={!canEditCanonicalIdentity || !splitValid || canonicalIdentityPending}
                    type="submit"
                  >
                    {canonicalIdentityPending ? <Loader2 className="spin" size={15} /> : <Split size={15} />}
                    <span>{t.detail.splitSelected}</span>
                  </button>
                </form>

                <form className="identity-action-form" onSubmit={submitIdentityRekey}>
                  <label>
                    <span>{t.detail.rekeyLabel}</span>
                    <input
                      maxLength={120}
                      value={identityDraft.rekeyLabel}
                      onChange={(event) => setIdentityDraft((current) => ({ ...current, rekeyLabel: event.target.value }))}
                    />
                  </label>
                  <button
                    className="secondary-button"
                    disabled={!canEditCanonicalIdentity || !rekeyValid || canonicalIdentityPending}
                    type="submit"
                  >
                    {canonicalIdentityPending ? <Loader2 className="spin" size={15} /> : <GitBranch size={15} />}
                    <span>{t.detail.rekeyIdentity}</span>
                  </button>
                </form>

                <form className="identity-action-form" onSubmit={submitIdentityMerge}>
                  <label>
                    <span>{t.detail.mergeTarget}</span>
                    <select
                      disabled={mergeTargetSuggestions.length === 0}
                      value={identityDraft.mergeTargetNodeId}
                      onChange={(event) =>
                        setIdentityDraft((current) => ({ ...current, mergeTargetNodeId: event.target.value }))
                      }
                    >
                      {mergeTargetSuggestions.length === 0 ? <option value="">{t.detail.noSameTypeTarget}</option> : null}
                      {mergeTargetSuggestions.map((suggestion) => (
                        <option key={suggestion.node.id} value={suggestion.node.id}>
                          {suggestion.node.label}
                          {suggestion.reasons.length > 0 ? ` - ${suggestion.reasons.join(", ")}` : ""}
                        </option>
                      ))}
                    </select>
                    {selectedMergeTargetSuggestion && selectedMergeTargetSuggestion.reasons.length > 0 ? (
                      <small className="identity-suggestion-note">
                        {t.detail.signalsPrefix(selectedMergeTargetSuggestion.reasons.join(", "))}
                      </small>
                    ) : null}
                  </label>
                  <button
                    className="secondary-button"
                    disabled={!canEditCanonicalIdentity || !selectedMergeTarget || canonicalIdentityPending}
                    type="submit"
                  >
                    {canonicalIdentityPending ? <Loader2 className="spin" size={15} /> : <GitMerge size={15} />}
                    <span>{t.detail.mergeIdentity}</span>
                  </button>
                </form>
              </div>
            ) : null}
          </section>

          <section className="detail-section">
            <p className="eyebrow">{t.detail.frameCommit}</p>
            <div className="commit-state">
              <CheckCircle2 size={18} />
              <strong>{readOnly ? t.detail.sharedStatus : isDefaultPreview ? t.detail.previewStatus : commit?.status ?? t.detail.idleStatus}</strong>
              <span>
                {readOnly ? t.detail.readOnlyGraphFrame : isDefaultPreview ? t.detail.defaultGraphFrame : commit?.nextAction ?? t.detail.waitingForGraphFrame}
              </span>
            </div>
          </section>
        </div>
      </details>
    </>
  );
}

function identityDraftFor(frame: GraphVisualizationFrame | null, node: GraphNode | null): CanonicalIdentityDraft {
  return {
    ...canonicalIdentityDraftFromNode(node),
    mergeTargetNodeId: canonicalMergeTargetSuggestions(frame, node)[0]?.node.id ?? ""
  };
}

function nodeDataFieldLabel(fieldKey: NodeDataFieldKey, t: Dictionary) {
  switch (fieldKey) {
    case "role":
      return t.detail.roleField;
    case "company":
      return t.detail.companyField;
    case "gender":
      return t.detail.genderField;
    case "birthday":
      return t.detail.birthdayField;
    case "interests":
      return t.detail.interestsField;
    case "email":
      return t.detail.emailField;
    case "phone":
      return t.detail.phoneField;
    case "location":
      return t.detail.locationField;
    case "stage":
      return t.detail.stageField;
    case "source":
      return t.detail.sourceField;
    case "industry":
      return t.detail.industryField;
    case "size":
      return t.detail.sizeField;
    case "revenue":
      return t.detail.revenueField;
    case "website":
      return t.detail.websiteField;
    case "timeText":
      return t.detail.timeTextField;
  }
}

// select values are stored verbatim; gender/industry map to localized labels,
// every other select (company size buckets) renders its language-neutral value.
function fieldOptionLabel(fieldKey: NodeDataFieldKey, value: string, t: Dictionary) {
  if (fieldKey === "gender") {
    switch (value) {
      case "male":
        return t.detail.genderOptions.male;
      case "female":
        return t.detail.genderOptions.female;
      case "other":
        return t.detail.genderOptions.other;
      case "undisclosed":
        return t.detail.genderOptions.undisclosed;
      default:
        return value;
    }
  }
  if (fieldKey === "industry") {
    const labels = t.detail.industryOptions as Record<string, string>;
    return labels[value] ?? value;
  }
  return value;
}

// Read-only "attributes" rows for the detail panel: only the current type's spec
// keys, only populated values, with select values shown as their localized label.
function nodeDisplayFields(node: GraphNode, t: Dictionary) {
  return nodeDataFieldSpecsByType[node.type].flatMap((spec) => {
    const raw = node.data[spec.key];
    const value =
      typeof raw === "string" ? raw.trim() : typeof raw === "number" || typeof raw === "boolean" ? String(raw) : "";
    if (!value) {
      return [];
    }
    const display = spec.kind === "select" ? fieldOptionLabel(spec.key, value, t) : value;
    return [{ key: spec.key, label: nodeDataFieldLabel(spec.key, t), value: display }];
  });
}

function detailTabLabel(t: Dictionary, tab: DetailTab) {
  switch (tab) {
    case "people":
      return t.entities.person;
    case "companies":
      return t.entities.organisation;
    case "events":
      return t.entities.event;
    case "opportunities":
      return t.entities.opportunity;
    case "notes":
      return t.entities.note;
  }
}

function identityActionLabel(action: CanonicalIdentityAction, t: Dictionary) {
  switch (action) {
    case "merge":
      return t.detail.identityActionMerge;
    case "rekey":
      return t.detail.identityActionRekey;
    case "split":
      return t.detail.identityActionSplit;
  }
}

function reviewReasonLabel(reason: ReviewReason, t: Dictionary) {
  switch (reason.type) {
    case "agent_flagged":
      return t.detail.reviewReasonAgentFlagged;
    case "low_confidence":
      return t.detail.reviewReasonLowConfidence(reason.confidencePercent);
    case "provider":
      return reason.text;
  }
}

function edgeEndpointLabel(edge: GraphEdge, selectedNodeId: string, frame: GraphVisualizationFrame | null) {
  const otherNodeId = edge.source === selectedNodeId ? edge.target : edge.source;
  return frame?.graph.nodes.find((candidate) => candidate.id === otherNodeId)?.label ?? otherNodeId;
}

function RelationshipIntelligencePanel({
  changeSetCreatePending,
  currentFrameRecordId,
  frame,
  node,
  onLogReminderTouchpoint,
  onOpenReminderFrame,
  onTargetChange,
  showReminders,
  state
}: {
  changeSetCreatePending: boolean;
  currentFrameRecordId: string | null;
  frame: GraphVisualizationFrame | null;
  node: GraphNode;
  onLogReminderTouchpoint: (edge: GraphEdge) => void;
  onOpenReminderFrame: (target: { frameRecordId: string; nodeId: string }) => void;
  onTargetChange: (nodeId: string) => void;
  showReminders: boolean;
  state: RelationshipIntelligenceState;
}) {
  const { t } = useI18n();
  const selectedTarget = state.targetNodes.find((candidate) => candidate.id === state.targetNodeId) ?? null;
  const canChooseTarget = state.targetNodes.length > 0;

  return (
    <section className="detail-section relationship-intel-panel" aria-label={t.relationshipIntel.ariaLabel}>
      <div className="section-heading-row">
        <p className="eyebrow">{t.relationshipIntel.title}</p>
      </div>

      {!state.canQuery ? (
        <p className="empty-line">{t.relationshipIntel.signInCommittedFrame}</p>
      ) : null}

      {state.canQuery && !canChooseTarget ? (
        <p className="empty-line">{t.relationshipIntel.addAnotherEntity}</p>
      ) : null}

      {state.canQuery && canChooseTarget ? (
        <label className="relationship-intel-target">
          <span>{t.relationshipIntel.targetLabel}</span>
          <select value={state.targetNodeId} onChange={(event) => onTargetChange(event.currentTarget.value)}>
            {state.targetNodes.map((targetNode) => (
              <option key={targetNode.id} value={targetNode.id}>
                {targetNode.label} · {entitySingularLabel(t, targetNode.type)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {state.canQuery && canChooseTarget ? (
        <div className="relationship-intel-group">
          <div className="relationship-intel-heading">
            <Route size={15} />
            <strong>{selectedTarget ? t.relationshipIntel.pathsTo(selectedTarget.label) : t.relationshipIntel.introductionPaths}</strong>
          </div>
          {state.pathStatus === "loading" ? (
            <p className="history-loading">
              <Loader2 className="spin" size={14} />
              {t.relationshipIntel.loadingPaths}
            </p>
          ) : null}
          {state.pathStatus === "error" ? (
            <p className="history-error">
              <AlertCircle size={14} />
              {state.pathError ?? t.relationshipIntel.pathsUnavailable}
            </p>
          ) : null}
          {state.pathStatus === "success" && state.paths.length === 0 ? (
            <p className="empty-line">{t.relationshipIntel.noPath}</p>
          ) : null}
          {state.paths.length > 0 ? (
            <div className="relationship-intel-list">
              {state.paths.map((path, index) => (
                <article className="relationship-intel-row" key={`${path.kind}-${index}`}>
                  <span className="relationship-intel-score">{Math.round(path.score * 100)}</span>
                  <span className="relationship-intel-main">
                    <strong>{path.nodes.map((pathNode) => pathNode.label).join(" -> ")}</strong>
                    <small>
                      {relationshipPathKindLabel(path.kind, t)} · {path.edges.map((edge) => edge.label).join(" / ")}
                      {(path.occurrenceCount ?? 1) > 1 ? ` · ${t.relationshipIntel.seenInFrames(path.occurrenceCount ?? 1)}` : null}
                    </small>
                  </span>
                </article>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {state.canQuery && showReminders ? (
        <div className="relationship-intel-group">
          <div className="relationship-intel-heading">
            <Bell size={15} />
            <strong>{t.relationshipIntel.staleReminders}</strong>
          </div>
          {state.reminderStatus === "loading" ? (
            <p className="history-loading">
              <Loader2 className="spin" size={14} />
              {t.relationshipIntel.loadingStale}
            </p>
          ) : null}
          {state.reminderStatus === "error" ? (
            <p className="history-error">
              <AlertCircle size={14} />
              {state.reminderError ?? t.relationshipIntel.remindersUnavailable}
            </p>
          ) : null}
          {state.reminderStatus === "success" && state.reminders.length === 0 ? (
            <p className="empty-line">
              {state.tenantReminderCount > 0
                ? t.relationshipIntel.noStaleForEntity(state.tenantReminderCount)
                : t.relationshipIntel.noStaleOlderThan(state.staleAfterDays)}
            </p>
          ) : null}
          {state.reminders.length > 0 ? (
            <div className="relationship-intel-list">
              {state.reminders.map((reminder) => {
                const action = reminderWorkspaceAction(reminder, currentFrameRecordId, frame, node.id);
                return (
                  <article className="relationship-intel-row" key={reminder.edge.id}>
                    <span className="relationship-intel-score">{Math.round(reminder.score * 100)}</span>
                    <span className="relationship-intel-main">
                      <strong>
                        {reminderOtherNodeLabel(reminder, node.id, frame)} · {reminder.edge.label}
                      </strong>
                      <small>
                        {t.relationshipIntel.lastSeenStaleBy(compactDateTime(reminder.lastSeenAt), reminder.staleByDays)}
                        {(reminder.occurrenceCount ?? 1) > 1 ? ` · ${t.relationshipIntel.seenInFrames(reminder.occurrenceCount ?? 1)}` : null}
                      </small>
                    </span>
                    {action.kind === "log_touchpoint" ? (
                      <button
                        className="secondary-button relationship-intel-action"
                        disabled={changeSetCreatePending}
                        onClick={() => onLogReminderTouchpoint(action.edge)}
                        title={t.relationshipIntel.recordTouchpointTitle}
                        type="button"
                      >
                        {changeSetCreatePending ? <Loader2 className="spin" size={13} /> : <CheckCircle2 size={13} />}
                        {t.relationshipIntel.logTouchpoint}
                      </button>
                    ) : (
                      <button
                        className="secondary-button relationship-intel-action"
                        onClick={() => onOpenReminderFrame({ frameRecordId: action.frameRecordId, nodeId: action.nodeId })}
                        title={t.relationshipIntel.openLastSeenFrameTitle}
                        type="button"
                      >
                        <Route size={13} />
                        {t.relationshipIntel.openInFrame}
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function relationshipPathKindLabel(kind: RelationshipIntelligenceState["paths"][number]["kind"], t: Dictionary) {
  return kind === "common_neighbor" ? t.relationshipIntel.commonNeighbor : t.relationshipIntel.direct;
}

function reminderOtherNodeLabel(
  reminder: RelationshipIntelligenceState["reminders"][number],
  selectedNodeId: string,
  frame: GraphVisualizationFrame | null
) {
  const otherNodeId = reminder.edge.source === selectedNodeId ? reminder.edge.target : reminder.edge.source;
  const projectedNode = reminder.edge.source === selectedNodeId ? reminder.targetNode : reminder.sourceNode;
  return projectedNode?.label ?? frame?.graph.nodes.find((candidate) => candidate.id === otherNodeId)?.label ?? otherNodeId;
}

export function ChangeSetHistoryPanel({
  auditExport,
  auditLog,
  commit,
  onApply,
  onExportAudit,
  onInspectAudit,
  onRefresh,
  privateAgentName,
  state
}: {
  auditExport: ChangeSetAuditExportState;
  auditLog: ChangeSetAuditLogState;
  commit: GraphFrameCommitResponse | null;
  onApply: (variables: ChangeSetActionVariables) => void;
  onExportAudit: (frameRecordId: string, changeSetId: string) => void;
  onInspectAudit: (frameRecordId: string, changeSetId: string) => void;
  onRefresh: (frameRecordId: string) => void;
  privateAgentName: string | null;
  state: ChangeSetHistoryState;
}) {
  const { t } = useI18n();
  const canRefresh = Boolean(commit);
  const items = state.status === "success" ? state.response.items : [];
  const [historyColumnVisibility, setHistoryColumnVisibility] = useColumnVisibility("change-set-history", {
    commands: false
  });
  const [auditColumnVisibility, setAuditColumnVisibility] = useColumnVisibility("change-set-audit", {});
  const historyRows = useMemo<ChangeSetHistoryTableRow[]>(
    () =>
      items.map((changeSet) => ({
        changeSet,
        commandCount: changeSet.commandCount,
        idLabel: shortChangeSetId(changeSet.id),
        reason: changeSet.reason ?? t.changeSetHistory.graphMutation,
        statusTime: changeSetStatusTime(changeSet)
      })),
    [items, t]
  );
  const auditRows = useMemo<AuditEventTableRow[]>(
    () =>
      auditLog.status === "success"
        ? auditLog.response.items.map((entry) => ({
            details: auditEventDetails(entry.event),
            entry,
            eventLabel: auditEventLabel(entry.eventType, t),
            idLabel: shortChangeSetId(entry.id),
            timeLabel: compactDateTime(entry.createdAt)
          }))
        : [],
    [auditLog, t]
  );
  const changeSetColumnLabels = useMemo(
    () => ({
      actions: t.changeSetHistory.columnActions,
      commands: t.changeSetHistory.columnCommands,
      changeSet: t.changeSetHistory.columnChangeSet,
      status: t.changeSetHistory.columnStatus,
      updated: t.changeSetHistory.columnUpdated
    }),
    [t]
  );
  const auditEventColumnLabels = useMemo(
    () => ({
      created: t.changeSetHistory.columnCreated,
      details: t.changeSetHistory.columnDetails,
      event: t.changeSetHistory.columnEvent
    }),
    [t]
  );
  const columns = useMemo<Array<DataTableColumnDef<ChangeSetHistoryTableRow>>>(
    () => [
      {
        accessorFn: (row) => row.reason,
        cell: ({ row }) => (
          <span className="history-row-main">
            <span className="history-row-title">
              <strong>{row.original.reason}</strong>
              <code>{row.original.idLabel}</code>
            </span>
            <span className="history-row-meta">
              {t.changeSetHistory.commandCount(row.original.commandCount)}
              {isPrivateAgentChangeSet(row.original.changeSet.clientMutationId) ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginLeft: 8 }}>
                  <AgentAvatar size="xs" srLabel={t.changeSetHistory.privateAgentSource} variant="private" />
                  <span>{privateAgentName ?? t.changeSetHistory.privateAgentSource}</span>
                </span>
              ) : null}
            </span>
          </span>
        ),
        enableHiding: false,
        header: t.changeSetHistory.columnChangeSet,
        id: "changeSet"
      },
      {
        accessorFn: (row) => row.changeSet.status,
        cell: ({ row }) => (
          <span className="history-status-cell">
            <span className={`history-status-icon history-status-icon-${row.original.changeSet.status}`} aria-hidden="true">
              {changeSetStatusIcon(row.original.changeSet.status)}
            </span>
            <span className={`history-status history-status-${row.original.changeSet.status}`}>
              {row.original.changeSet.status}
            </span>
          </span>
        ),
        header: t.changeSetHistory.columnStatus,
        id: "status"
      },
      {
        accessorFn: (row) => row.commandCount,
        cell: ({ row }) => row.original.commandCount,
        header: t.changeSetHistory.columnCommands,
        id: "commands",
        sortDescFirst: true
      },
      {
        accessorFn: (row) => changeSetStatusTimestamp(row.changeSet),
        cell: ({ row }) => row.original.statusTime,
        header: t.changeSetHistory.columnUpdated,
        id: "updated",
        sortDescFirst: true
      },
      {
        cell: ({ row }) => {
          const changeSet = row.original.changeSet;
          const isAuditOpen = auditLog.status !== "idle" && auditLog.changeSetId === changeSet.id;
          const isExportActive = auditExport.status !== "idle" && auditExport.changeSetId === changeSet.id;
          return (
            <span className="history-table-actions">
              {changeSet.status === "pending" && commit?.id === changeSet.frameRecordId ? (
                <button type="button" className="history-apply" onClick={() => onApply({
                  changeSetId: changeSet.id,
                  frameRecordId: changeSet.frameRecordId,
                  sourceLabel: row.original.reason
                })}>
                  {t.changeSet.apply}
                </button>
              ) : null}
              <button
                aria-label={t.changeSetHistory.exportAuditAriaLabel(row.original.idLabel)}
                className="history-export"
                disabled={!commit || (isExportActive && auditExport.status === "loading")}
                onClick={() => {
                  if (commit) {
                    onExportAudit(commit.id, changeSet.id);
                  }
                }}
                title={t.changeSetHistory.exportAuditTitle}
                type="button"
              >
                {isExportActive && auditExport.status === "loading" ? (
                  <Loader2 className="spin" size={14} />
                ) : (
                  <Download size={14} />
                )}
              </button>
              <button
                aria-expanded={isAuditOpen}
                aria-label={t.changeSetHistory.inspectAuditAriaLabel(row.original.idLabel)}
                className="history-drilldown"
                disabled={!commit || (isAuditOpen && auditLog.status === "loading")}
                onClick={() => {
                  if (commit) {
                    onInspectAudit(commit.id, changeSet.id);
                  }
                }}
                title={t.changeSetHistory.auditLogTitle}
                type="button"
              >
                {isAuditOpen && auditLog.status === "loading" ? (
                  <Loader2 className="spin" size={14} />
                ) : (
                  <List size={14} />
                )}
              </button>
            </span>
          );
        },
        enableHiding: false,
        enableSorting: false,
        header: t.changeSetHistory.columnActions,
        id: "actions"
      }
    ],
    [auditExport, auditLog, commit, onApply, onExportAudit, onInspectAudit, privateAgentName, t]
  );
  const auditColumns = useMemo<Array<DataTableColumnDef<AuditEventTableRow>>>(
    () => [
      {
        accessorFn: (row) => row.eventLabel,
        cell: ({ row }) => (
          <span className="audit-event-main">
            <span className="audit-event-title">
              <span className="audit-event-icon" aria-hidden="true">
                {auditEventIcon(row.original.entry.eventType)}
              </span>
              <strong>{row.original.eventLabel}</strong>
              <code>{row.original.idLabel}</code>
            </span>
          </span>
        ),
        enableHiding: false,
        header: t.changeSetHistory.columnEvent,
        id: "event"
      },
      {
        accessorFn: (row) => row.entry.createdAt,
        cell: ({ row }) => row.original.timeLabel,
        header: t.changeSetHistory.columnCreated,
        id: "created",
        sortDescFirst: true
      },
      {
        cell: ({ row }) => (
          <span className="audit-event-details">
            {row.original.details.map((detail) => (
              <span className="audit-chip" key={`${row.original.entry.id}-${detail.key}`}>
                <span>{detail.label}</span>
                <strong>{detail.value}</strong>
              </span>
            ))}
          </span>
        ),
        enableSorting: false,
        header: t.changeSetHistory.columnDetails,
        id: "details"
      }
    ],
    [t]
  );
  const auditChangeSetLabel = auditLog.status !== "idle" ? shortChangeSetId(auditLog.changeSetId) : "";

  return (
    <section className="detail-section change-set-history" aria-label={t.changeSetHistory.ariaLabel}>
      <div className="section-heading-row">
        <p className="eyebrow">{t.changeSetHistory.title}</p>
        <button
          aria-label={t.changeSetHistory.refreshAriaLabel}
          className="history-refresh"
          disabled={!canRefresh || state.status === "loading"}
          onClick={() => {
            if (commit) {
              onRefresh(commit.id);
            }
          }}
          title={t.changeSetHistory.refreshTitle}
          type="button"
        >
          {state.status === "loading" ? <Loader2 className="spin" size={14} /> : <Clock size={14} />}
        </button>
      </div>

      {!commit ? <p className="empty-line">{t.changeSetHistory.noCommit}</p> : null}
      {commit && state.status === "loading" ? (
        <p className="history-loading">
          <Loader2 className="spin" size={14} />
          {t.changeSetHistory.loading}
        </p>
      ) : null}
      {commit && state.status === "error" ? (
        <p className="history-error">
          <AlertCircle size={14} />
          {state.message}
        </p>
      ) : null}
      {commit && state.status === "success" && items.length === 0 ? (
        <p className="empty-line">{t.changeSetHistory.empty}</p>
      ) : null}
      {items.length > 0 ? (
        <DataTable
          className="history-table"
          columnLabels={changeSetColumnLabels}
          columnVisibility={historyColumnVisibility}
          columns={columns}
          data={historyRows}
          emptyLabel={t.changeSetHistory.empty}
          getRowId={(row) => row.changeSet.id}
          initialSorting={[{ desc: true, id: "updated" }]}
          labels={t.dataTable}
          onColumnVisibilityChange={setHistoryColumnVisibility}
        />
      ) : null}
      {auditExport.status === "success" ? (
        <p className="history-export-message">
          {t.changeSetHistory.downloaded(auditExport.filename)}
        </p>
      ) : null}
      {auditExport.status === "error" ? (
        <p className="history-error history-export-message">
          <AlertCircle size={14} />
          {auditExport.message}
        </p>
      ) : null}
      {auditLog.status !== "idle" ? (
        <div className="history-audit" aria-label={t.changeSetHistory.auditLogFor(auditChangeSetLabel)}>
          <div className="history-audit-heading">
            <List size={14} />
            <strong>{t.changeSetHistory.auditLog}</strong>
            <code>{auditChangeSetLabel}</code>
          </div>
          {auditLog.status === "loading" ? (
            <p className="history-loading">
              <Loader2 className="spin" size={14} />
              {t.changeSetHistory.loadingAuditLog}
            </p>
          ) : null}
          {auditLog.status === "error" ? (
            <p className="history-error">
              <AlertCircle size={14} />
              {auditLog.message}
            </p>
          ) : null}
          {auditLog.status === "success" ? (
            <DataTable
              className="audit-event-table"
              columnLabels={auditEventColumnLabels}
              columnVisibility={auditColumnVisibility}
              columns={auditColumns}
              data={auditRows}
              emptyLabel={t.changeSetHistory.emptyAuditLog}
              getRowId={(row) => row.entry.id}
              initialSorting={[{ desc: true, id: "created" }]}
              labels={t.dataTable}
              onColumnVisibilityChange={setAuditColumnVisibility}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * A change set carries its origin in `clientMutationId`. `personal-agent:<jobId>`
 * is written by exactly one producer — `POST /api/agent/research-jobs/:jobId/
 * materialize-change-set` (`apps/api/src/agent-job-routes.ts`) — so the chip is
 * a read of that fact, not an inference. Every other prefix (`csv-import:`,
 * `node-edit:`, `relationship-*`, `lead-stage:`, `timeline-replay:`) is a human
 * edit made in this app and gets no agent chip: there is no field that would
 * make one true.
 */
export function isPrivateAgentChangeSet(clientMutationId: string | undefined): boolean {
  return clientMutationId?.startsWith("personal-agent:") ?? false;
}

function changeSetStatusTime(changeSet: GraphChangeSetListResponse["items"][number]) {
  return compactDateTime(changeSetStatusTimestamp(changeSet));
}

function changeSetStatusTimestamp(changeSet: GraphChangeSetListResponse["items"][number]) {
  return changeSet.status === "reverted"
    ? changeSet.revertedAt ?? changeSet.appliedAt ?? changeSet.createdAt
    : changeSet.status === "applied"
      ? changeSet.appliedAt ?? changeSet.createdAt
      : changeSet.createdAt;
}

function changeSetStatusIcon(status: GraphChangeSetListResponse["items"][number]["status"]) {
  return status === "reverted" ? <Undo2 size={14} /> : status === "applied" ? <CheckCircle2 size={14} /> : <Clock size={14} />;
}

function auditEventIcon(eventType: GraphChangeSetAuditLogListResponse["items"][number]["eventType"]) {
  return eventType === "change_set.reverted" ? (
    <Undo2 size={13} />
  ) : eventType === "change_set.applied" ? (
    <CheckCircle2 size={13} />
  ) : (
    <Clock size={13} />
  );
}

function auditEventLabel(eventType: GraphChangeSetAuditLogListResponse["items"][number]["eventType"], t: Dictionary) {
  switch (eventType) {
    case "change_set.created":
      return t.changeSetHistory.auditCreated;
    case "change_set.applied":
      return t.changeSetHistory.auditApplied;
    case "change_set.reverted":
      return t.changeSetHistory.auditReverted;
  }
}

function auditEventDetails(event: GraphChangeSetAuditLogListResponse["items"][number]["event"]) {
  if (!event || typeof event !== "object" || Array.isArray(event)) {
    return [];
  }

  const priority = [
    "commandCount",
    "commandTypes",
    "inverseOpCount",
    "inverseOpTypes",
    "projectionStatus",
    "frameNodeCount",
    "frameEdgeCount",
    "reason",
    "clientMutationId"
  ];

  return Object.entries(event)
    .sort(([left], [right]) => {
      const leftIndex = priority.indexOf(left);
      const rightIndex = priority.indexOf(right);
      return (leftIndex === -1 ? priority.length : leftIndex) - (rightIndex === -1 ? priority.length : rightIndex);
    })
    .map(([key, value]) => ({
      key,
      label: auditEventKeyLabel(key),
      value: auditEventValue(value)
    }));
}

function auditEventKeyLabel(key: string) {
  return key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
}

function auditEventValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).join(", ");
  }
  if (value && typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}
